"use client";

import { useEffect, useRef, useState } from "react";
import {
  Search,
  Building,
  Filter,
  Trash2,
  Plus,
  Grid,
  List,
  Mail,
  Briefcase,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  X,
  Info,
  Calendar,
  ArrowDown,
  ArrowUp,
} from "lucide-react";
import Modal from "../layout/Modal";
import "./OrganizationsPage.css";
import Pagination from "../shared/paginator/paginator.js";
import { getBrandsAccess } from "../shared/access.js";
import CustomTooltip from "../shared/CustomTooltip";
import {
  getOnboardedOrganizationsApi,
  getIndustriesListApi,
  deleteOnboardedOrganizationApi,
  getConsoleUsersListApi,
} from "../../services/settingsService";

import { useNavigate } from "react-router-dom";
import BackButton from "../shared/backButton";
import { useTranslation } from "react-i18next";
import {
  formatEmployeeCreatedAt,
  handleApiError as baseHandleApiError,
} from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";

const OrganizationSkeletonListRow = () => {
  return (
    <div className="org-list-row org-skeleton-list-row">
      <div className="org-list-col">
        <div className="org-name-cell">
          <div className="org-avatar-small org-skeleton-avatar" />
          <div className="org-skeleton-line org-short" />
        </div>
      </div>
      <div className="org-list-col">
        <div className="org-skeleton-line" />
      </div>
      <div className="org-list-col">
        <div className="org-skeleton-line" />
      </div>
      <div className="org-list-col">
        <div className="org-skeleton-badge" />
      </div>
      <div className="org-list-col">
        <div className="org-skeleton-badge" />
      </div>
      <div className="org-list-col">
        <div className="org-skeleton-icon-btn" />
      </div>
    </div>
  );
};

const OrganizationSkeletonCard = () => {
  return (
    <div className="org-card org-skeleton-card">
      <div className="org-card-header">
        <div className="org-avatar org-skeleton-avatar" />
        <div className="org-skeleton-line org-short" />
        <div className="org-skeleton-line" />
      </div>
      <div className="org-details">
        <div className="org-skeleton-line" />
        <div className="org-skeleton-line" />
      </div>
      <div className="org-industry-section">
        <div className="org-skeleton-line org-short" />
        <div className="org-skeleton-tags">
          <div className="org-skeleton-tag" />
          <div className="org-skeleton-tag" />
        </div>
      </div>
    </div>
  );
};

const OrganizationsPage = () => {
  const { t } = useTranslation();
  const navigator = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("All");
  const [sortBy, setSortBy] = useState("Name");
  const brandsAccess = getBrandsAccess();
  // 1) One-time read of "my email" from sessionStorage
  const myEmail = (() => {
    try {
      const raw = sessionStorage.getItem("email");
      return raw ? raw.trim() : "";
    } catch {
      return "";
    }
  })();

  // 2) Make the select controlled with the initial value ready on first render
  const [filterOwner, setFilterOwner] = useState(myEmail);
  const [sortOrder, setSortOrder] = useState("asc");
  const [tableSortBy, setTableSortBy] = useState("organizationName");
  const [tableSortOrder, setTableSortOrder] = useState("asc");
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    organizationId: null,
  });
  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [organizations, setOrganizations] = useState([]);

  // PAGINATION LOGIC
  const [organizationsLength, setOrganizationsLength] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Pagination

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(organizationsLength / itemsPerPage);

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);

  const getAppliedFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterIndustry && filterIndustry !== "All") count++;
    if (sortBy && sortBy !== "Name") count++;
    if (filterOwner && filterOwner !== "") count++;
    return count;
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  useEffect(() => {
    if (!isSortDropdownOpen) return;

    const handlePointerDown = (event) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setIsSortDropdownOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsSortDropdownOpen(false);
        sortDropdownRef.current
          ?.querySelector(".org-sort-dropdown-trigger")
          ?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSortDropdownOpen]);

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  useEffect(() => {
    const init = async () => {
      await getAllUsersApiHandler();
      await getIndustriesListApiHandler();
      // await getOnboardedOrganizationsApiHandler();
    };
    init();
  }, []);

  // Refetch when page / filters / sort / search change
  useEffect(() => {
    getOnboardedOrganizationsApiHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    searchTerm,
    filterIndustry,
    sortBy,
    sortOrder,
    itemsPerPage,
    filterOwner,
  ]);

  // Reset to page 1 when inputs change (so you don't stay on an out-of-range page)
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterIndustry, sortBy, sortOrder]);

  // Clamp page if total count shrinks (e.g., after delete or filter)
  useEffect(() => {
    const pages = Math.max(
      1,
      Math.ceil((organizationsLength || 0) / itemsPerPage),
    );
    if (organizationsLength > 0 && currentPage > pages) {
      setCurrentPage(pages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationsLength]);

  const [industries, setIndustries] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const getIndustriesListApiHandler = async () => {
    try {
      const response = await getIndustriesListApi();
      setIndustries(["All", ...response.data]);
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const getOnboardedOrganizationsApiHandler = async () => {
    setIsLoading(true);
    try {
      const obj = {
        pageSize: itemsPerPage,
        pageNo: currentPage,
        search: searchTerm,
        industry: filterIndustry === "All" ? "" : filterIndustry,
        sort: sortBy,
        owner: filterOwner,
      };
      const response = await getOnboardedOrganizationsApi(obj);
      setOrganizations(response.data);
      setOrganizationsLength(response.totalCount);
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const [isLoading, setIsLoading] = useState(false);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);

    console.log("Fetching data for page:", currentPage);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const [viewMode, setViewMode] = useState("list"); // "card" or "list"

  const sortOptions = [
    { value: "Name", label: t("organizationsPage.organizationName") },
    { value: "Industry", label: t("organizationInfo.industry") },
    { value: "Size", label: t("organizationsPage.size") },
    {
      value: "Date Ascending",
      label: t("employees.listView.dateAscending"),
      icon: <ArrowUp size={12} />,
    },
    {
      value: "Date Descending",
      label: t("employees.listView.dateDescending"),
      icon: <ArrowDown size={12} />,
    },
  ];
  const selectedSortOption =
    sortOptions.find((option) => option.value === sortBy) || sortOptions[0];
  const focusSortOption = (index) => {
    const optionButtons = sortDropdownRef.current?.querySelectorAll(
      ".org-sort-dropdown-option",
    );
    optionButtons?.[index]?.focus();
  };
  const handleSortTriggerKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "Enter") {
      event.preventDefault();
      setIsSortDropdownOpen(true);
      requestAnimationFrame(() => {
        const selectedIndex = Math.max(
          0,
          sortOptions.findIndex((option) => option.value === sortBy),
        );
        focusSortOption(selectedIndex);
      });
    }
  };
  const handleSortOptionSelect = (option) => {
    setSortBy(option.value);
    setIsSortDropdownOpen(false);
  };
  const handleSortOptionKeyDown = (event, option, index) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const nextIndex =
        event.key === "ArrowDown"
          ? Math.min(index + 1, sortOptions.length - 1)
          : Math.max(index - 1, 0);
      focusSortOption(nextIndex);
    }

    if (event.key === "Home") {
      event.preventDefault();
      focusSortOption(0);
    }

    if (event.key === "End") {
      event.preventDefault();
      focusSortOption(sortOptions.length - 1);
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSortOptionSelect(option);
    }
  };

  const [ownerOptions, setOwnerOptions] = useState([]);

  // Mock API call - replace with actual API
  const getAllUsersApiHandler = async () => {
    try {
      // setIsLoading(true);
      // `${HOME_PATH}/getAllConsoleUsers?pageNo=${data.pageNo}&pageSize=${data.pageSize}&role=${data.role}&searchFilter=${data.searchFilter}&department=${data.department}&sortBy=${data.sortBy}`

      const obj = {
        pageNo: "",
        pageSize: "",
        role: "",
        searchFilter: "",
        department: "",
        sortBy: "",
      };

      const response = await getConsoleUsersListApi(obj);
      console.log("API Response:", response);
      setOwnerOptions(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      handleApiError(error, { showModal: false });
    } finally {
      // setIsLoading(false);
    }
  };

  const handleSort = (data) => {
    return [...data].sort((a, b) => {
      let aValue = a[sortBy.toLowerCase()];
      let bValue = b[sortBy.toLowerCase()];

      if (sortBy === "Size") {
        const sizeOrder = { Small: 1, Medium: 2, Large: 3, Enterprise: 4 };
        aValue = sizeOrder[aValue] || 0;
        bValue = sizeOrder[bValue] || 0;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleTableSort = (field) => {
    if (tableSortBy === field) {
      setTableSortOrder((currentOrder) =>
        currentOrder === "asc" ? "desc" : "asc",
      );
      return;
    }

    setTableSortBy(field);
    setTableSortOrder("asc");
  };

  const getTableSortIcon = (field) => {
    if (tableSortBy !== field) {
      return <ChevronsUpDown size={14} className="table-sort-icon" />;
    }

    return tableSortOrder === "asc" ? (
      <ChevronUp size={14} className="table-sort-icon active" />
    ) : (
      <ChevronDown size={14} className="table-sort-icon active" />
    );
  };

  const getOrganizationTableSortValue = (organization, field) => {
    switch (field) {
      case "organizationName":
        return organization.clientOrganization || "";
      case "admin":
        return organization.clientName || "";
      case "status":
        return organization.clientStatus || "";
      case "createdBy":
        return organization.createdBy || "";
      case "createdAt":
        return organization.createdAt
          ? new Date(organization.createdAt).getTime()
          : null;
      case "industry":
        return organization.clientIndustry || "";
      default:
        return "";
    }
  };

  const compareOrganizationTableValues = (aValue, bValue) => {
    const aIsEmpty = aValue === null || aValue === undefined || aValue === "";
    const bIsEmpty = bValue === null || bValue === undefined || bValue === "";

    if (aIsEmpty && bIsEmpty) return 0;
    if (aIsEmpty) return 1;
    if (bIsEmpty) return -1;

    const comparison =
      typeof aValue === "number" && typeof bValue === "number"
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, {
            numeric: true,
            sensitivity: "base",
          });

    return tableSortOrder === "asc" ? comparison : -comparison;
  };

  const sortedTableOrganizations = [...organizations].sort((a, b) =>
    compareOrganizationTableValues(
      getOrganizationTableSortValue(a, tableSortBy),
      getOrganizationTableSortValue(b, tableSortBy),
    ),
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "org-status-active";
      case "Pending":
        return "org-status-pending";
      default:
        return "org-status-inactive";
    }
  };

  const getPartnershipColor = (partnership) => {
    switch (partnership) {
      case "Premium":
        return "org-partnership-premium";
      case "Standard":
      default:
        return "org-partnership-basic";
    }
  };

  const handleOrganizationClick = (organization) => {
    navigator(`/brand/${organization._id}`);
  };

  const handleAddOrganization = () => {
    navigator("/brand/create");
  };

  const handleCopyInviteLink = () => {
    const inviteLink = "https://dev-console-v2.azurewebsites.net/brand/create";
    navigator.clipboard.writeText(inviteLink);
  };

  const handleDeleteOrganization = async (team) => {
    setShowModal({
      show: true,
      type: "warning",
      showButtons: true,
      title: t("organizationsPage.deleteBrand"),
      message: t("organizationsPage.deleteBrandConfirm", {
        brandName: team.clientOrganization,
      }),
      action: async () => {
        try {
          const response = await deleteOnboardedOrganizationApi(team._id);
          if (response.code === 200) {
            getOnboardedOrganizationsApiHandler();
            setShowModal({
              show: true,
              type: "success",
              title: t("common.success"),
              showButton: false,
              message: t("organizationsPage.brandDeleted", {
                brandName: team.clientOrganization,
              }),
            });
          }
        } catch (error) {
          setShowModal({
            show: true,
            type: "error",
            title: t("common.error"),
            showButton: false,
            message: t("organizationsPage.deleteBrandFailed"),
          });
        }
      },
    });
  };

  const handleSwitchToOrganization = (org) => {
    console.log("Switching to organization:", org);
    sessionStorage.setItem("tenantId", org.clientTenantId);
    sessionStorage.setItem("orgCode", org.clientOrgCode);
    sessionStorage.setItem("organization", org.clientOrganization);
    sessionStorage.setItem("selectedTenantId", org.clientTenantId);
    sessionStorage.setItem("selectedOrgCode", org.clientOrgCode);
    sessionStorage.setItem("selectedOrganization", org.clientOrganization);
    // window.location.reload(); // Reload to apply changes

    navigator("/home"); // Redirect to home or dashboard after switching
  };

  const checkSwitchDisabled = () => {
    return sessionStorage.getItem("email") == filterOwner;
  };

  const currentOrgName = sessionStorage.getItem("organization");

  const sanitizeSearch = (val) =>
    // allow letters, numbers, space, dash, underscore, and dot; strip everything else (incl. backslash)
    val.replace(/[\\`~!@#$%^&*()+={}[\]|:;"'<>,.?/]/g, "");

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterIndustry && filterIndustry !== "All") count++;
    if (sortBy && sortBy !== "Name") count++;
    if (filterOwner && filterOwner !== "") count++;
    return count;
  };

  const handleSearchButtonClick = () => {
    setCurrentPage(1);
    setSearchTerm(searchInputValue.trim());
    setShowSearchBar(false);
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  return (
    <div className="org-page">
      <main className="org-main">
        <BackButton />
        <div className="container">
          <div className="org-page-header">
            <div className="org-header-icon">
              <Building size={32} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              <h1>{t("organizationsPage.myBrands")}</h1>
              <CustomTooltip
                tooltip={{
                  title: t("organizationsPage.tooltips.title"),
                  message: t("organizationsPage.tooltips.message"),
                }}
                id="mobility-recommendation-info"
              >
                <Info
                  size={18}
                  style={{
                    color: "var(--text-secondary)",
                    cursor: "pointer",
                  }}
                />
              </CustomTooltip>
            </div>
            <p>{t("organizationsPage.pageDescription")}</p>
          </div>

          <div className="org-page-actions">
            <div
              className="org-view-toggle"
              style={{ position: "absolute", top: "105px", right: "260px" }}
            >
              <button
                className={`org-view-btn ${viewMode === "card" ? "active" : ""}`}
                onClick={() => setViewMode("card")}
                title={t("organizationsPage.cardView")}
              >
                <Grid size={16} />
              </button>
              <button
                className={`org-view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                title={t("organizationsPage.listView")}
              >
                <List size={16} />
              </button>
            </div>
            <button
              disabled={!brandsAccess.canCreateBrand}
              className="btn btn-primary create-team-btn"
              onClick={handleAddOrganization}
            >
              <Plus size={16} />
              {t("organizationsPage.addOrganization")}
            </button>
          </div>

          {/* Smart Search & Filter panel */}
          <div className="smart-filter-panel-upskill">
            <div className="smart-filter-top-bar">
              {showSearchBar ? (
                <div className="smart-search-inline-row">
                  <input
                    type="text"
                    placeholder={t("organizationsPage.searchPlaceholder")}
                    value={searchInputValue}
                    onChange={(e) =>
                      setSearchInputValue(sanitizeSearch(e.target.value))
                    }
                    className="smart-search-inline-input"
                    autoFocus
                  />
                  <span className="mobile-search-placeholder-hint">
                    {t("organizationsPage.searchPlaceholder")}
                  </span>
                  <button
                    className="smart-filter-icon-btn active"
                    title={t("common.search")}
                    onClick={handleSearchButtonClick}
                  >
                    <Search size={18} />
                  </button>
                  {searchTerm && (
                    <button
                      className="smart-search-clear"
                      onClick={handleClearSearch}
                      title={t("common.clear")}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ) : null}

              <div className="smart-filter-icons-right">
                {!showSearchBar && (
                  <button
                    className={`smart-filter-icon-btn ${showSearchBar ? "active" : ""}`}
                    onClick={() => {
                      setSearchInputValue(searchTerm);
                      setShowSearchBar(true);
                      setShowFiltersPanel(false);
                    }}
                    title={t("common.search")}
                  >
                    <Search size={18} />
                  </button>
                )}
                <div className="smart-filter-separator" />
                <button
                  className={`smart-filter-icon-btn ${showFiltersPanel ? "active" : ""}`}
                  onClick={() => {
                    setShowFiltersPanel(!showFiltersPanel);
                    if (!showFiltersPanel) setShowSearchBar(false);
                  }}
                  title={t("common.filters")}
                >
                  <Filter size={18} />
                </button>
              </div>

              <div className="smart-filter-info">
                {getAppliedFiltersCount() > 0 && (
                  <>
                    <span className="smart-filter-badge">
                      {getAppliedFiltersCount()}
                    </span>
                    <span>{t("common.filtersApplied")}</span>
                  </>
                )}
              </div>
            </div>

            {showFiltersPanel && (
              <>
                <div className="smart-filter-divider" />
                <div className="smart-filter-content">
                  <div className="smart-filter-grid">
                    <div className="smart-filter-item">
                      <label>{t("organizationInfo.industry")}:</label>
                      <select
                        value={filterIndustry}
                        onChange={(e) => setFilterIndustry(e.target.value)}
                        className={`smart-filter-select ${filterIndustry !== "All" ? "smart-active" : ""}`}
                      >
                        {industries.map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label id="org-sort-dropdown-label">
                        {t("organizationsPage.sortBy")}:
                      </label>
                      <div
                        className={`org-sort-dropdown ${sortBy !== "Name" ? "smart-active" : ""}`}
                        ref={sortDropdownRef}
                      >
                        <button
                          type="button"
                          className="org-sort-dropdown-trigger"
                          aria-haspopup="listbox"
                          aria-expanded={isSortDropdownOpen}
                          aria-labelledby="org-sort-dropdown-label org-sort-dropdown-value"
                          onClick={() =>
                            setIsSortDropdownOpen((isOpen) => !isOpen)
                          }
                          onKeyDown={handleSortTriggerKeyDown}
                        >
                          <span
                            className="org-sort-dropdown-value"
                            id="org-sort-dropdown-value"
                          >
                            <span>{selectedSortOption.label}</span>
                            {selectedSortOption.icon && (
                              <span className="org-sort-dropdown-icon">
                                {selectedSortOption.icon}
                              </span>
                            )}
                          </span>
                          <ChevronDown
                            size={14}
                            className={`org-sort-dropdown-chevron ${
                              isSortDropdownOpen ? "open" : ""
                            }`}
                          />
                        </button>

                        {isSortDropdownOpen && (
                          <div
                            className="org-sort-dropdown-menu"
                            role="listbox"
                            aria-labelledby="org-sort-dropdown-label"
                          >
                            {sortOptions.map((option, index) => (
                              <button
                                type="button"
                                key={option.value}
                                className={`org-sort-dropdown-option ${
                                  sortBy === option.value ? "selected" : ""
                                }`}
                                role="option"
                                aria-selected={sortBy === option.value}
                                onClick={() => handleSortOptionSelect(option)}
                                onKeyDown={(event) =>
                                  handleSortOptionKeyDown(event, option, index)
                                }
                              >
                                <span>{option.label}</span>
                                {option.icon && (
                                  <span className="org-sort-dropdown-icon">
                                    {option.icon}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("organizationInfo.whoCreated")}:</label>
                      <select
                        value={filterOwner}
                        onChange={(e) => setFilterOwner(e.target.value)}
                        className={`smart-filter-select ${filterOwner !== "" ? "smart-active" : ""}`}
                      >
                        {ownerOptions.map((option) => {
                          const val = (option.email || "").trim();
                          return (
                            <option key={val} value={val}>
                              {val}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {viewMode === "card" ? (
            <div className="org-grid">
              {organizations.map((organization) => (
                <div key={organization._id} className="org-card">
                  <div className="org-card-header">
                    <div
                      className="org-info"
                      onClick={() => handleOrganizationClick(organization)}
                    >
                      <div className="org-avatar">
                        <Building size={20} />
                      </div>
                      <div>
                        <h3>{organization?.clientOrganization}</h3>
                        <p className="org-industry">{organization?.industry}</p>
                      </div>
                    </div>
                  </div>

                  <div
                    className="org-details"
                    onClick={() => handleOrganizationClick(organization)}
                    style={{
                      textAlign: "left",
                      width: "100%",
                      justifyContent: "left",
                    }}
                  >
                    <div className="org-detail-row" style={{ width: "100%" }}>
                      <Mail size={14} />
                      <span>{organization?.clientEmail}</span>
                    </div>
                    {organization?.clientIndustry && (
                      <div className="org-detail-row" style={{ width: "100%" }}>
                        <Briefcase size={14} />
                        <span>
                          {t("organizationsPage.industry")}:{" "}
                          {organization?.clientIndustry}
                        </span>
                      </div>
                    )}

                    <div className="org-detail-row" style={{ width: "100%" }}>
                      <Calendar size={14} />
                      <span>
                        {formatEmployeeCreatedAt(organization?.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="org-list">
              <div className="org-list-header">
                <div
                  className="org-list-col table-sortable"
                  onClick={() => handleTableSort("organizationName")}
                >
                  {t("organizationsPage.organizationName")}
                  {getTableSortIcon("organizationName")}
                </div>
                <div
                  className="org-list-col table-sortable"
                  onClick={() => handleTableSort("admin")}
                >
                  {t("organizationsPage.admin")}
                  {getTableSortIcon("admin")}
                </div>
                <div
                  className="org-list-col table-sortable"
                  onClick={() => handleTableSort("status")}
                >
                  {t("organizationsPage.status")}
                  {getTableSortIcon("status")}
                </div>
                <div
                  className="org-list-col table-sortable"
                  onClick={() => handleTableSort("createdBy")}
                >
                  {t("organizationInfo.whoCreated")}
                  {getTableSortIcon("createdBy")}
                </div>
                <div
                  className="org-list-col table-sortable"
                  onClick={() => handleTableSort("createdAt")}
                >
                  {t("employees.listView.createdAt")}
                  {getTableSortIcon("createdAt")}
                </div>

                <div
                  className="org-list-col table-sortable"
                  onClick={() => handleTableSort("industry")}
                >
                  {t("organizationInfo.industry")}
                  {getTableSortIcon("industry")}
                </div>
                <div className="org-list-col org-switch-header-col">
                  {t("organizationsPage.tableHeaders.switch")}
                </div>
                <div className="org-list-col">
                  {t("organizationsPage.actions")}
                </div>
              </div>

              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <OrganizationSkeletonListRow key={index} />
                ))
              ) : organizations.length > 0 ? (
                sortedTableOrganizations.map((organization) => (
                  <div key={organization._id} className="org-list-row">
                    <div
                      className="org-list-col"
                      data-label={t("organizationsPage.organizationName")}
                    >
                      <div
                        className="org-name-cell"
                        onClick={() => handleOrganizationClick(organization)}
                      >
                        <div className="org-avatar-small">
                          <Building size={16} />
                        </div>
                        <span>
                          {organization?.clientOrganization}-(
                          {organization.clientPeopleCount})
                        </span>
                      </div>
                    </div>
                    <div
                      className="org-list-col"
                      data-label={t("organizationsPage.admin")}
                    >
                      {organization?.clientName ? organization.clientName : "-"}
                    </div>
                    <div
                      className="org-list-col"
                      data-label={t("organizationsPage.status")}
                    >
                      {organization?.clientStatus
                        ? organization.clientStatus
                        : "-"}
                    </div>
                    <div
                      className="org-list-col"
                      data-label={t("organizationInfo.whoCreated")}
                    >
                      {organization?.createdBy ? organization.createdBy : "-"}
                    </div>

                    <div
                      className="org-list-col"
                      data-label={t("employees.listView.createdAt")}
                    >
                      {formatEmployeeCreatedAt(organization?.createdAt)}
                    </div>

                    <div
                      className="org-list-col"
                      data-label={t("organizationInfo.industry")}
                    >
                      {organization?.clientIndustry ||
                        t("organizationsPage.notSpecified")}
                    </div>
                    <div
                      className="org-list-col org-switch-col"
                      data-label={t("organizationsPage.tableHeaders.switch")}
                    >
                      {organization.clientOrganization === currentOrgName ? (
                        <span className="org-current-badge">
                          {t("organizationsPage.currentOrganization")}
                        </span>
                      ) : (
                        (() => {
                          const isSwitchDisabled =
                            !checkSwitchDisabled() ||
                            !brandsAccess.canEditBrand;
                          const switchTitle = isSwitchDisabled
                            ? t("organizationsPage.permissionRequiredAction")
                            : t("organizationsPage.switchToOrganization");

                          const switchButton = (
                            <button
                              disabled={isSwitchDisabled}
                              className="org-switch-btn"
                              onClick={() =>
                                handleSwitchToOrganization(organization)
                              }
                              title={
                                !isSwitchDisabled ? switchTitle : undefined
                              }
                            >
                              <ArrowRight size={14} />
                            </button>
                          );

                          return isSwitchDisabled ? (
                            <span title={switchTitle}>{switchButton}</span>
                          ) : (
                            switchButton
                          );
                        })()
                      )}
                    </div>
                    <div
                      className="org-list-col"
                      data-label={t("organizationsPage.actions")}
                    >
                      {(() => {
                        const isDeleteDisabled = !brandsAccess.canDeleteBrand;
                        const deleteTitle = isDeleteDisabled
                          ? t("organizationsPage.permissionRequiredAction")
                          : t("organizationsPage.removeOrganization");

                        const deleteButton = (
                          <button
                            disabled={isDeleteDisabled}
                            className="org-delete-btn-small"
                            onClick={() => {
                              handleDeleteOrganization(organization);
                            }}
                            title={!isDeleteDisabled ? deleteTitle : undefined}
                          >
                            <Trash2 size={14} />
                          </button>
                        );

                        return isDeleteDisabled ? (
                          <span title={deleteTitle}>{deleteButton}</span>
                        ) : (
                          deleteButton
                        );
                      })()}
                    </div>
                  </div>
                ))
              ) : (
                <div className="org-no-results-message">
                  {t("organizationsPage.noOrganizationsFound")}
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {organizationsLength > 0 && (
            <Pagination
              loading={isLoading}
              total={organizationsLength}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              currentPageCount={organizations?.length}
              onPageChange={(page) => setCurrentPage(page)}
              pageSizeOptions={[10, 20, 50, 100]}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                const newTotalPages = Math.max(
                  1,
                  Math.ceil((organizationsLength || 0) / size),
                );
                if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
              }}
              infoRenderer={({ start, end, total }) =>
                t("organizationsPage.paginationInfo", { start, end, total })
              }
            />
          )}
        </div>
      </main>

      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({ show: false, type: "", title: "", message: "" })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={false}
      >
        {showModal.showButtons && (
          <div className="modal-actions">
            <button
              className="btn btn-outline"
              onClick={() =>
                setShowModal({ show: false, type: "", title: "", message: "" })
              }
            >
              {t("common.cancel")}
            </button>
            <button className="btn btn-primary" onClick={showModal.action}>
              {t("organizationsPage.delete")} {/* Custom delete button text */}
            </button>
          </div>
        )}
      </Modal>

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default OrganizationsPage;
